import React from 'react';
import { FormField, Panel, TextFormLabel, TextHelper } from '@allygpo/ally-ux';

interface PageHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Sits beside the subtitle, e.g. the strategy badge on Results. */
  meta?: React.ReactNode;
  actions?: React.ReactNode;
}

/** Page title (22px/400), muted subtitle and right-aligned actions. */
export const PageHeader: React.FC<PageHeaderProps> = ({ title, subtitle, meta, actions }) => (
  <header className="siq-page-header flex justify-content-between align-items-center u-gap-4 flex-wrap">
    <div className="siq-page-heading flex flex-column u-gap-1 min-w-0">
      <h1>{title}</h1>
      {(subtitle || meta) && <div className="flex align-items-center u-gap-2 flex-wrap text-xs text-tertiary">{subtitle && <span>{subtitle}</span>}{meta}</div>}
    </div>
    {actions && <div className="siq-page-actions flex u-gap-2 flex-wrap">{actions}</div>}
  </header>
);

interface CardProps {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Right side of the card header. */
  actions?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  /** Pad the body (forms). Tables leave it off so rows run edge to edge. */
  padded?: boolean;
  /** Inset primary bar on the left, as on the highest-NCR strip. */
  accent?: boolean;
  /** Adds Ally UX Panel's expand/collapse toggle; the subtitle then doubles as the collapsed summary. */
  collapsible?: boolean;
  /** Starts collapsed (with `collapsible`). */
  defaultCollapsed?: boolean;
}

/**
 * A section on the grey page. With a title or actions it's Ally UX's Panel (header band, then the body);
 * without, a plain bordered box from Ally Flex classes.
 */
export const Card: React.FC<CardProps> = ({ title, subtitle, actions, children, className, padded, accent, collapsible, defaultCollapsed }) => {
  const classes = ['siq-card', accent && 'is-accent', !padded && 'is-flush', className].filter(Boolean).join(' ');
  if (!title && !actions) {
    return <section className={`${classes} bg-base border-1 border-default radius-md overflow-hidden${padded ? ' u-p-4' : ''}`}>{children}</section>;
  }
  return (
    <Panel
      className={classes}
      header={<span className="flex flex-column u-gap-1 min-w-0">{title && <h2 className="siq-card-title">{title}</h2>}{subtitle && <span className="text-xs font-normal text-secondary">{subtitle}</span>}</span>}
      icons={actions ? <div className="siq-card-actions flex align-items-center u-gap-1 flex-wrap">{actions}</div> : undefined}
      toggleable={collapsible}
      collapsed={collapsible ? defaultCollapsed : undefined}
    >
      {children}
    </Panel>
  );
};

// Field width from Ally UX's FilterPanel recipe: full width on small screens, natural width from md up. (The
// recipe's flex-1 / 14–20rem stretch is left out: our dropdowns keep their own width, so it only spread them apart.)
const FILTER_FIELD_WIDTH = 'w-full md:w-auto';

/**
 * Filter panel: the fields that scope everything below it. Ally UX's FilterPanel recipe (AllyIQ layout), drawn
 * the way AllyIQ shows it: a grey, bordered, rounded panel. Each `Field` child gets the recipe's responsive width.
 */
export const FilterBand: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={`flex flex-row flex-wrap align-items-start u-p-4 u-gap-4 bg-surface-container border-1 border-default radius-md${className ? ` ${className}` : ''}`}>
    {React.Children.map(children, (child) => (React.isValidElement<{ className?: string }>(child)
      ? React.cloneElement(child, { className: [child.props.className, FILTER_FIELD_WIDTH].filter(Boolean).join(' ') })
      : child))}
  </div>
);

interface FieldProps {
  label: React.ReactNode;
  children: React.ReactNode;
  /** Helper line under the control. */
  help?: React.ReactNode;
  /** Right side of the label row, e.g. a Reset link. */
  labelAction?: React.ReactNode;
  /** Sits right after the label text, e.g. an ⓘ with its tooltip. */
  info?: React.ReactNode;
  className?: string;
  htmlFor?: string;
}

/** Ally UX FormField: label (plus ⓘ and a right-side action) above the control, helper text under it. */
export const Field: React.FC<FieldProps> = ({ label, children, help, labelAction, info, className, htmlFor }) => (
  <FormField className={`min-w-0${className ? ` ${className}` : ''}`}>
    {/* FormField spaces its direct children, so the label row is one child. */}
    <div className="flex align-items-center justify-content-between u-gap-2">
      <span className="flex align-items-center u-gap-1 min-w-0"><TextFormLabel htmlFor={htmlFor}>{label}</TextFormLabel>{info}</span>
      {labelAction}
    </div>
    {children}
    {help && <TextHelper>{help}</TextHelper>}
  </FormField>
);

